/** Labels and matching resolve the same registry and saved bindings. */
import {
  describeShortcut,
  getDefaultBinding,
  SHORTCUT_REGISTRY,
  shortcutEventCode,
  shortcutKeyboardLabel,
  shortcutActionLabel,
  shortcutPlatform,
  isShortcutModifier,
  type ShortcutBinding,
  type ShortcutId,
} from "./shortcut-registry";
import {
  isShortcutDisabled,
  isShortcutRecording,
  useShortcutStore,
} from "@/stores/shortcut-store";
import { translate } from "@/i18n";
import { libShortcutsMessages } from "@/i18n/messages/lib-shortcuts";

export type ShortcutDef = { label: string; keys: string[]; testId: string };
export function getShortcutBinding(id: ShortcutId): ShortcutBinding {
  return useShortcutStore.getState().overrides[id] ?? getDefaultBinding(id);
}
const GLYPHS: Record<string, string> = {
  Alt: "⌥",
  Control: "⌃",
  Shift: "⇧",
  Meta: "⌘",
};
/** One key as a cap or in a caption: a glyph for a macOS modifier, else
 *  the key's keyboard name ("Windows", "Shift", "1"). */
export function shortcutKeyCap(key: string): string {
  return (
    (shortcutPlatform() === "macos" && GLYPHS[key]) ||
    shortcutKeyboardLabel(key)
  );
}
/** A binding as key caps: one cap per key on macOS, one spelled-out cap on
 *  Windows and for a hold-and-tap binding. */
export function bindingKeyCaps(binding: ShortcutBinding): string[] {
  if (shortcutPlatform() === "windows" || binding.holdKeys?.length) {
    return [describeShortcut(binding)];
  }
  const keys = binding.keys.map(shortcutKeyCap);
  if (binding.gesture === "doubleTap") return [...keys, ...keys];
  if (binding.gesture === "hold" || binding.gesture === "drag")
    keys[0] = translate(libShortcutsMessages, "capHold", { key: keys[0]! });
  if (binding.gesture === "drag")
    keys.push(translate(libShortcutsMessages, "capDrag"));
  if (binding.gesture === "click")
    keys.push(translate(libShortcutsMessages, "capClick"));
  return keys;
}
export function shortcutKeys(testId: string): string[] {
  const item = SHORTCUT_REGISTRY.find((item) => item.testId === testId);
  return item ? bindingKeyCaps(getShortcutBinding(item.id)) : [];
}
/** Command+N (Windows+N) while dictation anywhere listens: stop with numbered
 *  prompt N. Fixed keys, not a registry shortcut. */
export function promptStopBinding(n: number): ShortcutBinding {
  return { keys: ["Meta", `Digit${n}`], gesture: "chord" };
}
export function denkerShortcuts(): ShortcutDef[] {
  return SHORTCUT_REGISTRY.map((item) => ({
    label: shortcutActionLabel(item.id),
    testId: item.testId,
    keys: shortcutKeys(item.testId),
  }));
}
export function spokenShortcut(testId: string): string {
  const item = SHORTCUT_REGISTRY.find((item) => item.testId === testId);
  return item ? describeShortcut(getShortcutBinding(item.id)) : "";
}
// Browser mouse events expose modifier families, so retain physical sides from
// key edges for side-specific recorded bindings. Blur clears lost key-up edges.
const physicalModifiers = new Set<string>();
if (typeof window !== "undefined") {
  window.addEventListener(
    "keydown",
    (event) => {
      const code = shortcutEventCode(event);
      if (isShortcutModifier(code)) physicalModifiers.add(code);
    },
    true,
  );
  window.addEventListener(
    "keyup",
    (event) => {
      physicalModifiers.delete(shortcutEventCode(event));
    },
    true,
  );
  window.addEventListener("blur", () => physicalModifiers.clear());
}
function sidesMatch(keys: string[], event?: KeyboardEvent): boolean {
  return keys.every(
    (key) =>
      !/(Left|Right)$/.test(key) ||
      physicalModifiers.has(key) ||
      (event !== undefined && shortcutEventCode(event) === key),
  );
}
type ModifierEvent = Pick<
  MouseEvent,
  "ctrlKey" | "altKey" | "shiftKey" | "metaKey"
>;
function modifierFamily(key: string): string {
  return key.replace(/(Left|Right)$/, "");
}
function modifiersMatch(keys: string[], event: ModifierEvent): boolean {
  const families = keys.map(modifierFamily);
  return (
    [
      ["Control", event.ctrlKey],
      ["Alt", event.altKey],
      ["Shift", event.shiftKey],
      ["Meta", event.metaKey],
    ] as const
  ).every(([key, pressed]) => families.includes(key) === pressed);
}
function codeMatches(key: string, code: string): boolean {
  return (
    key === code ||
    (isShortcutModifier(key) &&
      !key.endsWith("Left") &&
      !key.endsWith("Right") &&
      key === modifierFamily(code))
  );
}
export function shortcutModifierActive(
  id: ShortcutId,
  event: ModifierEvent,
): boolean {
  if (isShortcutRecording() || isShortcutDisabled(id)) return false;
  const binding = getShortcutBinding(id);
  return (
    binding.keys.every(isShortcutModifier) &&
    modifiersMatch(binding.keys, event) &&
    sidesMatch(binding.keys)
  );
}
export function matchesShortcut(id: ShortcutId, event: KeyboardEvent): boolean {
  if (isShortcutRecording() || isShortcutDisabled(id) || event.repeat)
    return false;
  if ((event as KeyboardEvent & { shortcutId?: string }).shortcutId === id)
    return true;
  const binding = getShortcutBinding(id);
  if (binding.gesture !== "chord") return false;
  if (!modifiersMatch(binding.keys, event) || !sidesMatch(binding.keys, event))
    return false;
  const code = shortcutEventCode(event);
  const nonModifiers = binding.keys.filter((key) => !isShortcutModifier(key));
  return nonModifiers.length
    ? nonModifiers.some((key) => codeMatches(key, code))
    : binding.keys.some((key) => codeMatches(key, code));
}
/** Browser fallback. Native desktop adapters own system-wide gestures. */
export function createShortcutKeyMatcher(
  id: ShortcutId,
): (event: KeyboardEvent) => boolean {
  let downAt: number | null = null;
  let lastTap: number | null = null;
  let signature = "";
  return (event) => {
    if (isShortcutRecording() || isShortcutDisabled(id)) {
      downAt = null;
      lastTap = null;
      return false;
    }
    const binding = getShortcutBinding(id);
    const nextSignature = JSON.stringify(binding);
    if (nextSignature !== signature) {
      downAt = null;
      lastTap = null;
      signature = nextSignature;
    }
    if (binding.gesture === "chord")
      return event.type === "keydown" && matchesShortcut(id, event);
    if (binding.gesture !== "doubleTap" || event.repeat) return false;
    const code = shortcutEventCode(event);
    const key = binding.keys[0];
    if (!key) return false;
    if (!codeMatches(key, code)) {
      if (event.type === "keydown") {
        downAt = null;
        lastTap = null;
      }
      return false;
    }
    const now = event.timeStamp;
    const keys = [...binding.keys, ...(binding.holdKeys ?? [])];
    if (event.type === "keydown") {
      if (!modifiersMatch(keys, event) || !sidesMatch(keys, event)) {
        downAt = null;
        lastTap = null;
        return false;
      }
      downAt = now;
      return false;
    }
    // The released modifier is absent from flags on keyup.
    const remaining = keys.filter(
      (other) => modifierFamily(other) !== modifierFamily(key),
    );
    if (
      downAt === null ||
      now - downAt > 250 ||
      !modifiersMatch(remaining, event) ||
      !sidesMatch(remaining, event)
    ) {
      downAt = null;
      lastTap = null;
      return false;
    }
    downAt = null;
    if (lastTap !== null && now - lastTap <= (binding.doubleTapMs ?? 450)) {
      lastTap = null;
      return true;
    }
    lastTap = now;
    return false;
  };
}
